import React from 'react';
import { getTemplateComponent } from '../../Billings/invoice-templates/TemplateRegistry';
import { InvoiceData, HospitalInfo } from '../../Billings/invoice-templates/types';

interface TemplatePreviewProps {
  templateId: string;
  selected: boolean;
  onSelect: () => void;
}

const getPreviewHospital = (): HospitalInfo => ({
  name: 'Medical Center',
  address: 'Dhaka, Bangladesh',
  phone: '+880 1711-000000',
  email: 'info@hospital.com',
  website: 'www.hospital.com',
  logo: undefined,
  headerImage: undefined,
});

const getPreviewInvoice = (): InvoiceData => ({
  id: 'preview-1',
  invoice_number: 'INV-2024-001',
  issue_date: new Date().toISOString(),
  due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  patient: {
    id: '1',
    name: 'John Doe',
    unique_id: 'P-001',
    phone: '+880 1711-123456',
    email: 'john@example.com',
    address: 'Dhaka, Bangladesh',
    age: 35,
    gender: 'Male',
  },
  items: [
    {
      id: '1',
      description: 'Consultation',
      quantity: 1,
      unit_price: 500,
      total: 500,
    },
    {
      id: '2',
      description: 'Blood Test',
      quantity: 1,
      unit_price: 800,
      total: 800,
    },
    {
      id: '3',
      description: 'X-Ray',
      quantity: 1,
      unit_price: 1200,
      total: 1200,
    },
  ],
  subtotal: 2500,
  discount_amount: 100,
  tax_amount: 0,
  total: 2400,
  amount_paid: 2400,
  balance_due: 0,
  status: 'paid',
  payment_method: 'Cash',
  notes: 'Thank you for choosing our services.',
});

export default function TemplatePreview({ templateId, selected, onSelect }: TemplatePreviewProps) {
  const TemplateComponent = getTemplateComponent(templateId);
  const previewHospital = getPreviewHospital();
  const previewInvoice = getPreviewInvoice();

  const isSmallFormat = templateId.includes('small');
  const scale = isSmallFormat ? 0.18 : 0.14;
  const frameWidth = isSmallFormat ? 140 : 240;
  const frameHeight = 340;

  return (
    <div
      onClick={onSelect}
      className={`relative bg-white rounded-lg border-2 overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
        selected ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 hover:border-primary-300'
      }`}
      style={{ width: frameWidth, height: frameHeight }}
    >
      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-2 right-2 z-10 bg-primary-600 text-white rounded-full p-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}

      {/* Miniature template preview */}
      <div
        className="absolute top-0 left-0"
        style={{
          width: isSmallFormat ? '80mm' : '210mm',
          height: isSmallFormat ? 'auto' : '297mm',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <TemplateComponent invoice={previewInvoice} hospital={previewHospital} />
      </div>

      {/* Overlay gradient for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
    </div>
  );
}
